import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Buildings, FolderOpen, MagnifyingGlass, Ticket } from "@phosphor-icons/react";
import api from "../lib/api";

const GROUPS = [
  { key: "tickets", label: "Tickets", icon: Ticket },
  { key: "projects", label: "Projects", icon: FolderOpen },
  { key: "organizations", label: "Clients", icon: Buildings },
];

// Topbar quick search. The backend scopes results to the caller's role;
// this component only renders whatever comes back.
export default function GlobalSearch() {
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const seqRef = useRef(0);

  useEffect(() => {
    const query = q.trim();
    if (query.length < 2) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    const seq = ++seqRef.current;
    const t = setTimeout(() => {
      api.get("/search", { params: { q: query } })
        .then((r) => {
          if (seqRef.current !== seq) return;
          setResults(r.data);
          setActive(-1);
        })
        .catch(() => seqRef.current === seq && setResults(null))
        .finally(() => seqRef.current === seq && setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // navigating away closes and clears the box
  useEffect(() => {
    setOpen(false);
    setQ("");
    setResults(null);
  }, [location.pathname]);

  const flat = results
    ? GROUPS.flatMap(({ key }) => (results[key] || []).map((item) => ({ type: key, item })))
    : [];

  const goTo = (row) => {
    if (!row) return;
    setOpen(false);
    setQ("");
    if (row.type === "tickets") navigate(`/tickets/${row.item.id}`);
    else if (row.type === "projects") navigate(`/projects/${row.item.id}`);
    else navigate(`/organizations/${row.item.id}`);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!open || flat.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      goTo(flat[Math.max(active, 0)]);
    }
  };

  const offsets = {};
  let acc = 0;
  for (const { key } of GROUPS) {
    offsets[key] = acc;
    acc += results ? (results[key] || []).length : 0;
  }
  const showPanel = open && q.trim().length >= 2;

  return (
    <div className="hidden md:block relative flex-1 max-w-xl" ref={boxRef} data-testid="global-search">
      <div className="flex items-center gap-3">
        <MagnifyingGlass size={16} className="text-slate-400 shrink-0" />
        <input
          type="text"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Quick search tickets, projects, clients…"
          className="w-full bg-transparent outline-none text-sm placeholder:text-slate-400"
          data-testid="topbar-search-input"
        />
      </div>
      {showPanel && (
        <div
          className="absolute left-0 right-0 top-full mt-3 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-30"
          data-testid="search-results"
        >
          {loading && !results && <div className="px-4 py-3 text-sm text-slate-400">Searching…</div>}
          {!loading && results && flat.length === 0 && (
            <div className="px-4 py-3 text-sm text-slate-400" data-testid="search-empty">
              No matches in your workspace.
            </div>
          )}
          {results && GROUPS.map(({ key, label, icon: Icon }) => {
            const items = results[key] || [];
            if (items.length === 0) return null;
            return (
              <div key={key} className="py-1 border-t border-slate-100 first:border-t-0">
                <div className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-[0.15em] text-slate-400 font-semibold">
                  {label}
                </div>
                {items.map((item, j) => {
                  const i = offsets[key] + j;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2.5 ${
                        i === active ? "bg-blue-50 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                      }`}
                      onMouseEnter={() => setActive(i)}
                      onClick={() => goTo({ type: key, item })}
                      data-testid="search-result-item"
                    >
                      <Icon size={14} className="text-slate-400 shrink-0" />
                      {key === "tickets" ? (
                        <span className="truncate">
                          <span className="text-slate-400 mr-1.5">{item.ticket_no}</span>
                          {item.subject}
                        </span>
                      ) : (
                        <span className="truncate">{item.name}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
